import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useQuery } from "@apollo/client";

import { useAuth } from "../auth-provider";
import { LDMultiKindContext, useLDClient } from "launchdarkly-react-client-sdk";
import { OrganizationFragment, OrganizationQuery } from "gql/graphql";
import ORGANIZATION_QUERY from "./networking/organization";

interface IContextProps {
  organization?: OrganizationFragment;
  refreshOrganization: () => void;
}

const OrganizationContext = createContext<IContextProps>({
  organization: undefined,
  refreshOrganization: () => {},
});

export interface IProviderProps {
  children?: ReactNode;
}

export const OrganizationProvider = ({ children }: IProviderProps) => {
  const { isLoggedIn } = useAuth();
  const { me } = useAuth();

  const { data, refetch } = useQuery<OrganizationQuery>(ORGANIZATION_QUERY, {
    skip: !isLoggedIn,
  });

  const organization = data?.organization;

  const value = useMemo<IContextProps>(
    () => ({
      organization,
      refreshOrganization: () => refetch(),
    }),
    [organization, refetch],
  );

  const launchDarkly = useLDClient();

  useEffect(() => {
    if (!organization?.id || !me?.id) {
      return;
    }

    const ldContext: LDMultiKindContext = {
      kind: "multi",
      org: {
        key: organization.id,
      },
      user: {
        key: me.id,
      },
    };
    launchDarkly?.identify(ldContext);
  }, [launchDarkly, organization?.id, me?.id]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => useContext(OrganizationContext);
